import { FC, useContext, useEffect, useState } from "react";
import { Box, Button, Colors, FormControlLabel } from "@analyticsMD/quil";
import APIContext from "../../contexts/APIContext";
import { useMutation, useQueryClient } from "react-query";
import {
  endRoundingSession,
  patchRoundingPatientEntry,
  postRoundingPatientEntry,
  postRoundingSession,
} from "../../api/endpoints";
import moment from "moment";
import CheckBox from "@mui/material/Checkbox";
import { ActionTrack } from "../../types/Analytics";

const sxStyles = {
  roundingWrapper: {
    display: "flex",
    justifyContent: "end",
  },
  button: {
    backgroundColor: Colors.qpurple_alt_0,
    textTransform: "none",
    paddingLeft: "1rem",
    paddingRight: "1rem",
    "&:hover": {
      backgroundColor: Colors.qpurple_alt_0,
    },
    color: Colors.grey_80,
  },
  checkbox: {
    color: Colors.white,
    padding: "0.25rem",
    "&.Mui-checked": {
      color: Colors.white,
    },
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
};

interface RoundingButtonProps {
  hideRoundingButton?: boolean; // for the sidebar patient list view, we need to hide the button. but leave the checkbox
}

const RoundingButton: FC<RoundingButtonProps> = ({
  hideRoundingButton = false,
}) => {
  const {
    activeRoundingSessionQuery,
    patientID,
    setShowErrorToast,
    analytics,
  } = useContext(APIContext);
  const queryClient = useQueryClient();

  const activeRoundingSession = activeRoundingSessionQuery.isSuccess
    ? activeRoundingSessionQuery.data
    : null;

  const roundingPatientEntry = activeRoundingSession
    ? activeRoundingSession.patient_entries.find(
        (entry) => entry.patient === patientID
      )
    : null;

  const [roundingPatientEntryId, setRoundingPatientEntryId] = useState(
    roundingPatientEntry?.id
  );

  const [patientRounded, setPatientRounded] = useState(
    roundingPatientEntry ? roundingPatientEntry.is_rounded : true
  );

  const endRoundingSessionMutation = useMutation(endRoundingSession, {
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["roundingsession"] });
    },
    onError: () => {
      setShowErrorToast(true);
    },
  });

  const postRoundingSessionMutation = useMutation(postRoundingSession, {
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["roundingsession"] });
      analytics?.track(ActionTrack.ROUNDING_SESSION_STARTED, {
        session_id: data.id,
        emr_user_id: data.emr_user_id,
      });
    },
    onError: () => {
      setShowErrorToast(true);
    },
  });

  const patchRoundingPatientEntryMutation = useMutation(
    patchRoundingPatientEntry,
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries({ queryKey: ["roundingpatiententry"] });
        setPatientRounded(data.is_rounded);
      },
      onError: () => {
        setShowErrorToast(true);
      },
    }
  );

  const postRoundingPatientEntryMutation = useMutation(
    postRoundingPatientEntry,
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries({ queryKey: ["roundingpatiententry"] });
        setRoundingPatientEntryId(data.id);
        setPatientRounded(true);
      },
      onError: () => {
        setShowErrorToast(true);
      },
    }
  );

  const createPatientEntry = (rounding_session_id) => {
    postRoundingPatientEntryMutation.mutate({
      patient: patientID,
      is_rounded: true,
      rounded_timestamp: moment().toISOString(true),
      rounding_session: rounding_session_id,
    });
    setPatientRounded(true);
  };

  // if we have an active session, but there's no entry for this patient, we need to create one
  // currently lint ignoring this, will resolve this properly in the second round of work in sprint 25
  useEffect(() => {
    if (!activeRoundingSession) {
      setRoundingPatientEntryId(null);
    }
    if (activeRoundingSession && !roundingPatientEntryId) {
      createPatientEntry(activeRoundingSession.id);
    }
    // eslint-disable-next-line
  }, [activeRoundingSession]);

  const handleStartRounds = () => {
    postRoundingSessionMutation.mutate();
  };

  const handleEndRounds = () => {
    const payload = {
      id: activeRoundingSession.id,
      end_time: moment().toISOString(true),
    };
    endRoundingSessionMutation.mutate(payload);

    analytics?.track(ActionTrack.ROUNDING_SESSION_ENDED, {
      session_id: activeRoundingSession.id,
      emr_user_id: activeRoundingSession.emr_user_id,
      end_time: payload.end_time,
    });
  };

  const handleTogglePatientRounded = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const isPatientRounded = event.target.checked;

    const payload = {
      id: roundingPatientEntryId,
      is_rounded: isPatientRounded,
      rounded_timestamp: isPatientRounded ? moment().toISOString(true) : null,
      rounding_session: activeRoundingSession.id,
    };

    patchRoundingPatientEntryMutation.mutate(payload);

    setPatientRounded(isPatientRounded);

    if (isPatientRounded) {
      analytics?.track(ActionTrack.ROUNDING_SESSION_PATIENT_REVIEWED, {
        session_id: activeRoundingSession.id,
        emr_user_id: activeRoundingSession.emr_user_id,
        patient: patientID,
        rounded_timestamp: payload.rounded_timestamp,
      });
    } else {
      analytics?.track(ActionTrack.ROUNDING_SESSION_PATIENT_UNREVIEWED, {
        session_id: activeRoundingSession.id,
        emr_user_id: activeRoundingSession.emr_user_id,
        patient: patientID,
      });
    }
  };

  return (
    <Box sx={sxStyles.roundingWrapper}>
      {!hideRoundingButton && !activeRoundingSession && (
        <Button sx={sxStyles.button} onClick={handleStartRounds}>
          Start rounds
        </Button>
      )}
      {activeRoundingSession && (
        <Box>
          <FormControlLabel
            control={
              <CheckBox
                checked={patientRounded}
                sx={sxStyles.checkbox}
                onChange={handleTogglePatientRounded}
              />
            }
            label="Patient reviewed"
          />
          {!hideRoundingButton && (
            <Button sx={sxStyles.button} onClick={handleEndRounds}>
              End rounds
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

export default RoundingButton;
